import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';

import { HrDayValidation } from '@app/shared/_models/hr';
import { EffectiveSlot, deltaLabel, hoursLabel } from '../../hr-schedule-engine';
import { HrTimeSource } from '../../hr-payroll';
import { WorkPeriod } from '../../hr-timesheet';
import { DayCloseRow, buildDayCloseRows, changedDayCloseRows, dayCloseTotals, paidHoursOf, proposedPaidMinutes, setPaidHours } from '../../hr-validation';

// Clôture d'UNE journée, tous salariés — le geste de fin de journée du mode planning.
// Le planning a annoncé la journée ; ici on dit ce qui s'est réellement passé : « Marc est resté
// une heure de plus, Sophie n'est pas venue ». Ce qui sort d'ici est ce qui part en paie.
export interface DayCloseDialogData {
	dat: string;
	dayLabel: string;						// « Mardi 28/07 »
	storeName: string;
	employees: { id: number; name: string }[];
	slots: EffectiveSlot[];
	periods: WorkPeriod[];			// vide sans pointeuse — la colonne « badgé » n'apparaît alors pas
	validations: HrDayValidation[];
	source: HrTimeSource;
}

@Component({
	selector: 'app-hr-day-close-dialog',
	standalone: true,
	imports: [FormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule,
		MatIconModule, MatCheckboxModule, MatTooltipModule],
	templateUrl: './day-close-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './day-close-dialog.component.scss',
})
export class DayCloseDialogComponent {

	public data: DayCloseDialogData = inject(MAT_DIALOG_DATA);
	private dialogRef = inject(MatDialogRef<DayCloseDialogComponent>);

	public rows: DayCloseRow[] = [];

	constructor() {
		this.rows = buildDayCloseRows(
			this.data.dat, this.data.employees, this.data.slots,
			this.data.periods, this.data.validations, this.data.source);
	}

	get totals() {
		return dayCloseTotals(this.rows);
	}

	// Sans badgeuse, la colonne n'aurait que des tirets.
	get showBadged(): boolean {
		return this.data.source === 'clock';
	}

	h(min: number): string {
		return hoursLabel(min);
	}

	// Saisie en heures décimales (7,5) : c'est ainsi qu'on raisonne en paie.
	paidHours(row: DayCloseRow): number {
		return paidHoursOf(row);
	}

	onPaidHoursChange(row: DayCloseRow, value: any): void {
		setPaidHours(row, value);
	}

	deltaLabel(row: DayCloseRow): string {
		return deltaLabel(row.delta_min);
	}

	// « Il n'est pas venu » : zéro heure payée, la ligne reste tracée avec son motif.
	onAbsentChange(row: DayCloseRow): void {
		if (row.absent) {
			row.paid_min = 0;
		} else {
			row.paid_min = proposedPaidMinutes(row.planned_min, row.badged_min, this.data.source);
		}
		row.delta_min = row.paid_min - row.planned_min;
	}

	// Le cas courant : la journée s'est passée comme prévu pour tout le monde.
	// Les journées DÉCLARÉES par le salarié sont épargnées : ramener silencieusement au planning
	// quelqu'un qui vient de signaler une heure de plus viderait le geste de son sens. Le gérant
	// garde bien sûr la main dessus, ligne par ligne.
	onConformeAuPlanning(): void {
		for (const r of this.rows) {
			if (r.absent || r.declared) continue;
			r.paid_min = proposedPaidMinutes(r.planned_min, r.badged_min, this.data.source);
			r.delta_min = r.paid_min - r.planned_min;
			r.validated = true;
		}
	}

	// On ne renvoie QUE ce qui a bougé : re-clôturer une journée déjà arbitrée ne doit pas
	// reposter un event par salarié.
	onSave(): void {
		for (const r of this.rows) r.validated = true;
		this.dialogRef.close(changedDayCloseRows(this.rows, this.data.validations));
	}

	onCancel(): void {
		this.dialogRef.close(undefined);
	}
}
